import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message } from "antd";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import type { DictionaryDto } from "../../../../lib/interfaces";
import { ITypesCharacteristicsListSearch } from "../../types/TypesCharacteristicsListSearch";
import {
  addValuesTypesCharacteristicsRoute,
  createTypesCharacteristicsRoute,
  deleteValuesTypesCharacteristicsRoute,
  getTypesCharacteristicsDicRoute,
  getTypesCharacteristicsDictionaryTypesRoute,
  getTypesCharacteristicsObjectTypesDicRoute,
  searchTypesCharacteristicsByIdRoute,
  searchTypesCharacteristicsObjectRoute,
  searchTypesCharacteristicsRoute,
} from "./typesCharacteristicsRoutes";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { camelizeString } from "../../utils/camelizeString";
import { ITypesCharacteristicsCreateParams } from "../../types/TypesCharacteristicsCreateParams";
import { IDeleteValuesTypesCharacteristicsParams } from "../../types/DeleteValuesTypesCharacteristicsParams";
import { IAddValuesTypesCharacteristicsParams } from "../../types/AddValuesTypesCharacteristicsParams";
import { ITypesCharacteristicsObjectListSearch } from "../../../TypesCharacteristicsObject/types/TypesCharacteristicsObjectListSearch";
import { TypeCharacteristicObject } from "../../../TypesCharacteristicsObject/types/TypeCharacteristicObject";

interface InitialState {
  typesCharacteristics: TypeCharacteristic[];
  typesCharacteristicsLoading: LoadingStatusCode;
  typesCharacteristicsObjects: TypeCharacteristicObject[];
  typesCharacteristicsObjectsLoading: LoadingStatusCode;
  typesCharacteristicsTypesDataDic: DictionaryDto[];
  typesCharacteristicsTypesDataDicLoading: LoadingStatusCode;
  typesCharacteristicsObjectTypesDic: DictionaryDto[];
  typesCharacteristicsObjectTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsPagination: {
    current: number;
    total: number;
  };
  typesCharacteristicsTotalElements: number;
  typesCharacteristicsTypesDic: DictionaryDto[];
  typesCharacteristicsTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsTypesValuesDic: DictionaryDto[];
  typesCharacteristicsTypesValuesDicLoading: LoadingStatusCode;
  typesCharacteristicsTypesUnitsDic: DictionaryDto[];
  typesCharacteristicsTypesUnitsDicLoading: LoadingStatusCode;
  typesCharacteristicsRangeTypesDic: DictionaryDto[];
  typesCharacteristicsRangeTypesDicLoading: LoadingStatusCode;
  typesCharacteristicsById?: TypeCharacteristic;
  typesCharacteristicsByIdLoading: LoadingStatusCode;
  createTypesCharacteristicsLoading: LoadingStatusCode;
  deleteValuesTypesCharacteristicsLoading: LoadingStatusCode;
  deleteAllValuesTypesCharacteristicsLoading: LoadingStatusCode;
  addValuesTypesCharacteristicsLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  typesCharacteristics: [],
  typesCharacteristicsLoading: "idle",
  typesCharacteristicsObjects: [],
  typesCharacteristicsObjectsLoading: "idle",
  typesCharacteristicsTypesDataDic: [],
  typesCharacteristicsTypesDataDicLoading: "idle",
  typesCharacteristicsObjectTypesDic: [],
  typesCharacteristicsObjectTypesDicLoading: "idle",
  typesCharacteristicsPagination: {
    current: 0,
    total: 0,
  },
  typesCharacteristicsTotalElements: 0,
  typesCharacteristicsTypesDic: [],
  typesCharacteristicsTypesDicLoading: "idle",
  typesCharacteristicsTypesValuesDic: [],
  typesCharacteristicsTypesValuesDicLoading: "idle",
  typesCharacteristicsTypesUnitsDic: [],
  typesCharacteristicsTypesUnitsDicLoading: "idle",
  typesCharacteristicsRangeTypesDic: [],
  typesCharacteristicsRangeTypesDicLoading: "idle",
  typesCharacteristicsById: undefined,
  typesCharacteristicsByIdLoading: "idle",
  createTypesCharacteristicsLoading: "idle",
  deleteValuesTypesCharacteristicsLoading: "idle",
  deleteAllValuesTypesCharacteristicsLoading: "idle",
  addValuesTypesCharacteristicsLoading: "idle",
};

// Получение справочника типов данных
export const fetchTypesCharacteristicsTypesDataDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesDataDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("data_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов данных. Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов значений
export const fetchTypesCharacteristicsTypesValuesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesValueDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("value_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов значений. Попробуйте позже",
      });
    }
  }
);

// Получение справочника единиц измерения
export const fetchTypesCharacteristicsTypesUnitsDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesUnitsDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("price_unit"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка единиц измерения. Попробуйте позже",
      });
    }
  }
);

// Получение справочника границ диапазона
export const fetchTypesCharacteristicsRangeTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsRangeTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute("range_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка границ диапазона. Попробуйте позже",
      });
    }
  }
);

// Получение всех типов словарей
export const fetchTypesCharacteristicsTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsDictionaryTypesRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов словарей. Попробуйте позже",
      });
    }
  }
);

// Получение информации о переданном справочнике
export const fetchTypesCharacteristicsSelectValueDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsSelectValueDic",
  async (type: string) => {
    try {
      const response = axios.get(getTypesCharacteristicsDicRoute(type));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки информации о выбранном справочнике. Попробуйте позже",
      });
    }
  }
);

// Поиск типов характеристик
export const searchTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristics",
  async (searchParams: ITypesCharacteristicsListSearch) => {
    try {
      const response = axios.get(searchTypesCharacteristicsRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки типов характеристик. Попробуйте позже",
      });
    }
  }
);

// Поиск типов характеристик (объект)
export const searchTypesCharacteristicsObject = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristicsObject",
  async (searchParams: ITypesCharacteristicsObjectListSearch) => {
    try {
      const response = axios.get(searchTypesCharacteristicsObjectRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки типов характеристик (объект). Попробуйте позже",
      });
    }
  }
);

// Получение справочника типов данных
export const fetchTypesCharacteristicsObjectTypesDic = createAsyncThunk(
  "typesCharacteristics/fetchTypesCharacteristicsObjectTypesDic",
  async () => {
    try {
      const response = axios.get(getTypesCharacteristicsObjectTypesDicRoute());
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов объектов. Попробуйте позже",
      });
    }
  }
);

// Получение информации о Типе Характеристики
export const searchTypesCharacteristicsById = createAsyncThunk(
  "typesCharacteristics/searchTypesCharacteristicsById",
  async (typesCharacteristicsId: string) => {
    try {
      const response = axios.get(searchTypesCharacteristicsByIdRoute(typesCharacteristicsId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки информации о типе характеристики. Попробуйте позже",
      });
    }
  }
);

// Создание Типа Характеристики
export const createTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/createTypesCharacteristics",
  async (params: ITypesCharacteristicsCreateParams) => {
    try {
      const response = axios.post(createTypesCharacteristicsRoute(), params);
      const { data } = await response;

      message.success("Тип характеристики успешно создан");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка создания типа характеристики. Попробуйте позже",
      });
    }
  }
);

// Удаление Значения Типа Характеристики
export const deleteValuesTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/deleteValuesTypesCharacteristics",
  async (deleteParams: IDeleteValuesTypesCharacteristicsParams) => {
    try {
      const response = axios.delete(deleteValuesTypesCharacteristicsRoute(deleteParams));
      const { data } = await response;

      message.destroy();
      message.success("Значение типа характеристики успешно удалено");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка удаления значения типа характеристики. Попробуйте позже",
      });
    }
  }
);

// Удаление Значения Типа Характеристики
export const deleteAllValuesTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/deleteAllValuesTypesCharacteristics",
  async (deleteParams: IDeleteValuesTypesCharacteristicsParams) => {
    try {
      const response = axios.delete(deleteValuesTypesCharacteristicsRoute(deleteParams));
      const { data } = await response;

      message.destroy();
      message.success("Значения типа характеристики успешно удалены");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка удаления значений типа характеристики. Попробуйте позже",
      });
    }
  }
);

// Добавление Значения Типа Характеристики
export const addValuesTypesCharacteristics = createAsyncThunk(
  "typesCharacteristics/addValuesTypesCharacteristics",
  async (params: IAddValuesTypesCharacteristicsParams) => {
    try {
      const response = axios.post(
        addValuesTypesCharacteristicsRoute(params.charTypeId),
        params.body
      );
      const { data } = await response;

      message.destroy();
      message.success("Значение типа характеристики успешно добавлено");

      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка добавления значения типа характеристики. Попробуйте позже",
      });
    }
  }
);

// // Получение версий политики
// export const fetchPolicyVersions = createAsyncThunk(
//   "policies/fetchPolicyVersions",
//   async (policyId: number) => {
//     try {
//       const response = axios.get(fetchPolicyVersionsRoute(policyId));
//       const { data } = await response;
//       return data;
//     } catch (e) {
//       return catchApiError({
//         e,
//         title: "Ошибка загрузки загрузки политик. Попробуйте позже",
//       });
//     }
//   }
// );

// // Поиск группы предложения для параметров создания
// export const searchOfferingGroupForPolicyCreate = createAsyncThunk(
//   "policies/searchOfferingGroupForPolicyCreate",
//   async (offeringGroupid: number) => {
//     try {
//       const response = axios.get(offeringGroupIdSearchRoute(offeringGroupid));
//       const { data } = await response;
//       return data;
//     } catch (e) {
//       return catchApiError({
//         e,
//         title: "Ошибка загрузки загрузки политик. Попробуйте позже",
//       });
//     }
//   }
// );

// // Получение предложения по id
// export const fetchOfferById = createAsyncThunk<Offer, { id: number; onDate: string }>(
//   "policies/fetchOfferById",
//   async (params) => {
//     try {
//       const response = axios.get<Offer>(fetchOfferByIdRoute(params));
//       const { data } = await response;
//       return data;
//     } catch (e) {
//       return catchApiError({
//         e,
//         title: "Ошибка загрузки предложения. Попробуйте позже",
//       });
//     }
//   }
// );

// // Обновление политики
// export const updatePolicy = createAsyncThunk(
//   "policies/updatePolicy",
//   async (
//     {
//       params,
//       body,
//     }: {
//       params: {
//         policyId: number;
//         params: { fromDate?: string; force: boolean };
//       };
//       body: any;
//     },
//     { dispatch }
//   ) => {
//     try {
//       await axios.patch(updatePolicyRoute(params), body);

//       message.success("Успешно обновлено");

//       return Promise.all([
//         dispatch(searchPolicy(params.policyId)),
//         dispatch(fetchPolicyVersions(params.policyId!)),
//       ]);
//     } catch (e) {
//       return catchApiError({
//         e,
//         title: "Ошибка обновления. Попробуйте позже",
//       });
//     }
//   }
// );

// // Обновление даты закрытия политики
// export const updateCloseDatePolicy = createAsyncThunk(
//   "policies/updateCloseDatePolicy",
//   async (
//     params: {
//       policyId: number;
//       onDate?: string;
//     },
//     { dispatch }
//   ) => {
//     try {
//       await axios.delete(updateCloseDatePolicyRoute(params));

//       message.success("Дата закрытия успешно обновлена");

//       return Promise.all([
//         dispatch(searchPolicy(params.policyId)),
//         dispatch(fetchPolicyVersions(params.policyId!)),
//       ]);
//     } catch (e) {
//       return catchApiError({
//         e,
//         title: "Ошибка обновления даты закрытия. Попробуйте позже",
//       });
//     }
//   }
// );

export const typesCharacteristicsSlice = createSlice({
  name: "typesCharacteristics",
  initialState,
  reducers: {
    // cleanPoliciesList: (state) => {
    //   state.policiesListLoading = "idle";
    //   state.policiesList = [];
    //   state.policiesListTotalElements = 0;
    //   state.policiesListPagination = {
    //     current: 0,
    //     total: 0,
    //   };
    // },
    // cleanPolicyVersions: (state) => {
    //   state.policyVersionsLoading = "idle";
    //   state.policyVersions = [];
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchTypesCharacteristics.pending, (state) => {
        state.typesCharacteristicsLoading = "pending";
      })
      .addCase(searchTypesCharacteristics.rejected, (state) => {
        state.typesCharacteristicsLoading = "rejected";
        state.typesCharacteristics = [];
        state.typesCharacteristicsTotalElements = 0;
        state.typesCharacteristicsPagination = {
          current: 0,
          total: 0,
        };
      })
      .addCase(searchTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsLoading = "fulfilled";
        state.typesCharacteristics = payload?.content;
        state.typesCharacteristicsTotalElements = payload.totalElements;
        state.typesCharacteristicsPagination = {
          current: payload.number,
          total: payload.totalElements,
        };
      })
      .addCase(searchTypesCharacteristicsObject.pending, (state) => {
        state.typesCharacteristicsObjectsLoading = "pending";
      })
      .addCase(searchTypesCharacteristicsObject.rejected, (state) => {
        state.typesCharacteristicsObjectsLoading = "rejected";
        state.typesCharacteristicsObjects = [];
      })
      .addCase(searchTypesCharacteristicsObject.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectsLoading = "fulfilled";
        state.typesCharacteristicsObjects = payload;
      })
      .addCase(fetchTypesCharacteristicsObjectTypesDic.pending, (state) => {
        state.typesCharacteristicsObjectTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsObjectTypesDic.rejected, (state) => {
        state.typesCharacteristicsObjectTypesDicLoading = "rejected";
        state.typesCharacteristicsObjectTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsObjectTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsObjectTypesDicLoading = "fulfilled";
        state.typesCharacteristicsObjectTypesDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.pending, (state) => {
        state.typesCharacteristicsTypesDataDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.rejected, (state) => {
        state.typesCharacteristicsTypesDataDicLoading = "rejected";
        state.typesCharacteristicsTypesDataDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesDataDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesDataDicLoading = "fulfilled";
        state.typesCharacteristicsTypesDataDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.pending, (state) => {
        state.typesCharacteristicsTypesValuesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.rejected, (state) => {
        state.typesCharacteristicsTypesValuesDicLoading = "rejected";
        state.typesCharacteristicsTypesValuesDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesValuesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesValuesDicLoading = "fulfilled";
        state.typesCharacteristicsTypesValuesDic = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.pending, (state) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.rejected, (state) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "rejected";
        state.typesCharacteristicsTypesUnitsDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesUnitsDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesUnitsDicLoading = "fulfilled";
        state.typesCharacteristicsTypesUnitsDic = payload;
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.pending, (state) => {
        state.typesCharacteristicsRangeTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.rejected, (state) => {
        state.typesCharacteristicsRangeTypesDicLoading = "rejected";
        state.typesCharacteristicsRangeTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsRangeTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsRangeTypesDicLoading = "fulfilled";
        state.typesCharacteristicsRangeTypesDic = payload;
      })
      .addCase(searchTypesCharacteristicsById.pending, (state) => {
        state.typesCharacteristicsByIdLoading = "pending";
      })
      .addCase(searchTypesCharacteristicsById.rejected, (state) => {
        state.typesCharacteristicsByIdLoading = "rejected";
        state.typesCharacteristicsById = undefined;
      })
      .addCase(searchTypesCharacteristicsById.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsByIdLoading = "fulfilled";
        state.typesCharacteristicsById = payload;
      })
      .addCase(fetchTypesCharacteristicsTypesDic.pending, (state) => {
        state.typesCharacteristicsTypesDicLoading = "pending";
      })
      .addCase(fetchTypesCharacteristicsTypesDic.rejected, (state) => {
        state.typesCharacteristicsTypesDicLoading = "rejected";
        state.typesCharacteristicsTypesDic = [];
      })
      .addCase(fetchTypesCharacteristicsTypesDic.fulfilled, (state, { payload }) => {
        state.typesCharacteristicsTypesDicLoading = "fulfilled";
        state.typesCharacteristicsTypesDic = payload;
      })
      .addCase(
        fetchTypesCharacteristicsSelectValueDic.pending,
        (state, { meta: { arg: dicTypeAlias } }) => {
          const camelizeDicTypeAlias = camelizeString(dicTypeAlias);
          state[`${camelizeDicTypeAlias}Loading`] = "pending";
        }
      )
      .addCase(
        fetchTypesCharacteristicsSelectValueDic.rejected,
        (state, { meta: { arg: dicTypeAlias } }) => {
          const camelizeDicTypeAlias = camelizeString(dicTypeAlias);
          state[`${camelizeDicTypeAlias}Loading`] = "rejected";
        }
      )
      .addCase(fetchTypesCharacteristicsSelectValueDic.fulfilled, (state, action) => {
        const dicTypeAlias = action.meta.arg;
        const camelizeDicTypeAlias = camelizeString(dicTypeAlias);

        state[`${camelizeDicTypeAlias}Loading`] = "fulfilled";
        state[camelizeDicTypeAlias] = action.payload;
      })
      .addCase(createTypesCharacteristics.pending, (state) => {
        state.createTypesCharacteristicsLoading = "pending";
      })
      .addCase(createTypesCharacteristics.rejected, (state) => {
        state.createTypesCharacteristicsLoading = "rejected";
      })
      .addCase(createTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.createTypesCharacteristicsLoading = "fulfilled";
      })
      .addCase(deleteValuesTypesCharacteristics.pending, (state) => {
        state.deleteValuesTypesCharacteristicsLoading = "pending";
      })
      .addCase(deleteValuesTypesCharacteristics.rejected, (state) => {
        state.deleteValuesTypesCharacteristicsLoading = "rejected";
      })
      .addCase(deleteValuesTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.deleteValuesTypesCharacteristicsLoading = "fulfilled";
      })
      .addCase(deleteAllValuesTypesCharacteristics.pending, (state) => {
        state.deleteAllValuesTypesCharacteristicsLoading = "pending";
      })
      .addCase(deleteAllValuesTypesCharacteristics.rejected, (state) => {
        state.deleteAllValuesTypesCharacteristicsLoading = "rejected";
      })
      .addCase(deleteAllValuesTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.deleteAllValuesTypesCharacteristicsLoading = "fulfilled";
      })
      .addCase(addValuesTypesCharacteristics.pending, (state) => {
        state.addValuesTypesCharacteristicsLoading = "pending";
      })
      .addCase(addValuesTypesCharacteristics.rejected, (state) => {
        state.addValuesTypesCharacteristicsLoading = "rejected";
      })
      .addCase(addValuesTypesCharacteristics.fulfilled, (state, { payload }) => {
        state.addValuesTypesCharacteristicsLoading = "fulfilled";
      });
  },
});

export const {
  reducer: typesCharacteristicsReducer,
  actions: typesCharacteristicsActions,
} = typesCharacteristicsSlice;
